<template>
  <div class="dashboard-view">
    <section class="dashboard-section">
      <div class="dashboard-section-title">Resumo</div>
      <div class="indicators-grid">
        <IndicatorCard
          v-for="indicator in mainIndicators"
          :key="indicator.key"
          v-bind="indicator"
        />
      </div>
    </section>

    <section class="dashboard-section">
      <div class="dashboard-agent-detail">
        <div class="dashboard-agent-detail-title">
          <v-icon size="15" color="#8a929b">mdi-source-branch</v-icon>
          Detalhamento · {{ agentName }}
        </div>
        <SubAgentPanel
          v-for="subAgent in activeAgentSubAgents"
          :key="subAgent.key"
          :sub-agent="subAgent"
          :executions="subAgentExecutions[subAgent.key] || []"
          :range="range"
          :base="base"
          :disabled="
            runningAgentKey !== null && runningAgentKey !== subAgent.key
          "
          @run-start="runningAgentKey = $event"
          @run-end="runningAgentKey = null"
        />
        <p
          v-if="!activeAgentSubAgents.length"
          class="dashboard-agent-detail-empty"
        >
          Este agente não tem sub-agentes configurados.
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import { pipelineSteps, resolverSteps } from "@/data/mockData.js";
import {
  getAgents,
  getIndicators,
  getExecutions,
} from "@/services/nexiaApi.js";
import IndicatorCard from "./Partials/IndicatorCard/IndicatorCard.vue";
import SubAgentPanel from "./Partials/SubAgentPanel/SubAgentPanel.vue";

const STEPS_BY_KEY = {
  "pipeline-cvc": pipelineSteps,
  "resolver-cvc": resolverSteps,
};

const EMPTY_METRICS = {
  exportadas: 0,
  importadas: 0,
  jaExistiam: 0,
  erro: 0,
  pendentes: 0,
  pendentesAgente: 0,
  pendentesManual: 0,
  pendentesNovas: 0,
  pendentesPreexistentes: 0,
  pendentesChegaram: 0,
  resolvidas: 0,
};

export default {
  name: "Dashboard",
  components: { IndicatorCard, SubAgentPanel },
  props: {
    range: { type: Object, required: true },
    agent: { type: String, default: null },
    agentName: { type: String, default: "" },
    base: { type: String, default: null },
  },
  data() {
    return {
      agentsList: [],
      subAgentExecutions: {},
      metrics: EMPTY_METRICS,
      runningAgentKey: null,
      loading: false,
    };
  },
  computed: {
    mainIndicators() {
      const m = this.metrics;
      return [
        {
          key: "exportadas",
          label: "Reservas exportadas do portal da CVC",
          value: m.exportadas,
          icon: "mdi-file-export-outline",
          color: "secondary",
        },
        {
          key: "jaExistiam",
          label: "Reservas existentes no Phoenix",
          value: m.jaExistiam,
          icon: "mdi-content-duplicate",
          color: "secondary",
        },
        {
          key: "importadas",
          label: "Reservas importadas no Phoenix sem pendência",
          value: m.importadas,
          icon: "mdi-check-circle-outline",
          color: "success",
        },
        {
          key: "resolvidas",
          label: "Reservas pendentes resolvidas",
          value: m.resolvidas,
          icon: "mdi-check-decagram-outline",
          color: "primary",
        },
        {
          key: "pendentes",
          label: "Reservas em pendência",
          value: m.pendentes,
          icon: "mdi-clock-alert-outline",
          color: "warning",
          tooltipLines: this.pendentesTooltipLines,
        },
        {
          key: "erro",
          label: "Reservas com erro na importação",
          value: m.erro,
          icon: "mdi-alert-circle-outline",
          color: "error",
        },
      ];
    },
    // Hover do card "Reservas em pendência" (valor = em pendência AGORA no
    // Phoenix). O valor do card não fecha com a soma das exportadas (as que já
    // existiam e continuam pendentes também estão em "existentes"), então o
    // tooltip explica de onde vem: quantas CHEGARAM com pendência nas
    // execuções (gravado na hora) e quantas dessas ainda estão abertas, x as
    // que já estavam pendentes no Phoenix antes dessas execuções.
    // Sem tooltip quando não há nada a detalhar.
    pendentesTooltipLines() {
      const m = this.metrics;
      if (!m.pendentes && !m.pendentesChegaram) return [];
      const plural = (n, um, varios) => (n === 1 ? um : varios);
      const lines = [`Reservas em pendência atualmente: ${m.pendentes}`];
      if (m.pendentesChegaram > 0) {
        lines.push(
          `${m.pendentesChegaram} ${plural(
            m.pendentesChegaram,
            "reserva chegou",
            "reservas chegaram"
          )} com pendência nestas execuções; ${m.pendentesNovas} ${plural(
            m.pendentesNovas,
            "delas ainda está",
            "delas ainda estão"
          )} em pendência.`
        );
      }
      if (m.pendentesPreexistentes > 0) {
        lines.push(
          `${m.pendentesPreexistentes} ${plural(
            m.pendentesPreexistentes,
            "já estava",
            "já estavam"
          )} em pendência no Phoenix antes dessas execuções.`
        );
      }
      const semClassificacao =
        m.pendentes - m.pendentesNovas - m.pendentesPreexistentes;
      if (semClassificacao > 0) {
        lines.push(
          `${semClassificacao} sem classificação (anterior a esta atualização)`
        );
      }
      return lines;
    },
    currentAgent() {
      return this.agentsList.find((a) => a.key === this.agent) || null;
    },
    activeAgentSubAgents() {
      if (!this.currentAgent) return [];
      return this.currentAgent.subAgents.map((sa) => ({
        ...sa,
        steps: STEPS_BY_KEY[sa.key] || [],
        currentStepIndex: null,
      }));
    },
  },
  watch: {
    range() {
      this.loadRangeData();
    },
    agent() {
      this.loadRangeData();
    },
    base() {
      this.loadRangeData();
    },
  },
  async created() {
    await this.loadAgents();
    await this.loadRangeData();
  },
  methods: {
    async loadAgents() {
      try {
        this.agentsList = await getAgents();
      } catch (err) {
        console.error("Falha ao carregar agentes da API", err);
        this.agentsList = [];
      }
    },
    async loadRangeData() {
      this.loading = true;
      const pipelineKey = `pipeline-${this.agent}`;
      const resolverKey = `resolver-${this.agent}`;
      try {
        const [indicators, pipelineExecs, resolverExecs] = await Promise.all([
          getIndicators(this.range, this.agent, this.base),
          getExecutions(pipelineKey, this.range, this.base),
          getExecutions(resolverKey, this.range, this.base),
        ]);
        this.metrics = indicators;
        this.subAgentExecutions = {
          [pipelineKey]: pipelineExecs,
          [resolverKey]: resolverExecs,
        };
      } catch (err) {
        console.error("Falha ao carregar dados do período na API", err);
        this.metrics = EMPTY_METRICS;
        this.subAgentExecutions = {};
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style src="./styles.scss" scoped lang="scss" />
